import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, setTokens } from "../api.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Button } from "../components/ui/button.js";

export default function RegisterPage({ onRegister, onBack }: { onRegister: () => void; onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Ingresa un correo electrónico");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ token: string; refreshToken: string }>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password, nombre: nombre.trim() || undefined }),
      });
      setTokens(res.token, res.refreshToken);
      onRegister();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al registrar";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="FolioListo" className="h-24 w-24" />
            <div>
              <CardTitle className="text-3xl font-heading">Crear cuenta</CardTitle>
              <CardDescription>Regístrate en FolioListo</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/25 text-destructive text-sm font-medium flex items-start gap-2">
                <span className="shrink-0 mt-0.5">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre (opcional)</Label>
              <Input
                id="nombre"
                type="text"
                placeholder="Tu nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                className={`transition-colors ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña (mín. 8 caracteres)</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                className={`transition-colors ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                required
                minLength={8}
              />
            </div>
            <Button type="submit" variant="cta" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                "Crear cuenta"
              )}
            </Button>
            <button
              type="button"
              onClick={onBack}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ¿Ya tienes cuenta? Inicia sesión
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
